import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { PageTitle } from 'ui/PageTitle';
import { FluidButton } from 'ui/FluidButton';
import { Table } from 'ui/Table';
import { Pagination } from 'pureUi/Pagination';
import { LoadingBar, ErrorBar } from 'ui/NetworkStatusBar';
import { Multiselect } from 'ui/Multiselect';
import { TextInput } from 'ui/TextInput';
import SingleDateInput from 'pureUi/SingleDateInput';
import { ConfirmationModal, EConfirmationModalType } from 'ui/ConfirmationModal';
import { isAdmin, isFinanceUser } from 'store/modules/auth/selectors';
import { usePagination } from 'hooks/usePagination';
import { useDebounce } from 'hooks/useDebounce';
import { useModal } from 'hooks/useModal';
import { useNotifications } from 'hooks/useNotifications';
import { ESortOrder } from 'store/common/types';
import { ITableColumn, ITableRow } from 'ui/Table/types';
import {
  useBankTransactionsQuery,
  usePaymentMethodGroupsQuery,
  BANK_TRANSACTIONS_QUERY_KEY,
  PAYMENT_METHOD_GROUPS_QUERY_KEY,
} from './hooks/useBankTransactionsQueries';
import { useDeleteBankTransaction } from './hooks/useDeleteBankTransaction';
import { useExportBankTransactions } from './hooks/useExportBankTransactions';
import {
  IBankTransaction,
  isDebit,
  EBankTransactionProcessingStatus,
  IBankTransactionFilters,
  TBankTransactionTypeFilter,
} from './types';
import { TransactionTypeCell } from './components/TransactionTypeCell';
import { TransactionStatusCell } from './components/TransactionStatusCell';
import { TruncatedTooltipCell } from './components/TruncatedTooltipCell';
import { BankCell } from './components/BankCell';
import { ActionsCell } from './components/ActionsCell';

/**
 * Format transaction date as "DD MMM YY" (e.g., "20 Feb 25")
 */
const formatTransactionDate = (dateString: string): string => {
  const date = parseISO(dateString);
  return format(date, 'dd MMM yy');
};

/**
 * Format amount from cents to display string with thousands separator
 */
const formatAmount = (amount: number): string => {
  const value = Math.abs(amount) / 100;
  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

/**
 * Get results count text for page title
 */
const getResultsText = (count: number): string => {
  if (!count) return '(no results found)';
  if (count === 1) return '(1 result found)';
  return `(${count} results found)`;
};

/**
 * Table column definitions
 */
const tableColumns: ITableColumn[] = [
  { id: 'date', label: 'Date', width: '100px', fieldToSortBy: 'transactionDate' },
  { id: 'bank', label: 'Bank', width: '150px' },
  { id: 'type', label: 'Type', width: '100px' },
  { id: 'reference', label: 'Reference', width: '100px' },
  { id: 'narration', label: 'Narration', width: '200px' },
  { id: 'counterparty', label: 'Counterparty', width: '120px', fieldToSortBy: 'counterparty' },
  { id: 'status', label: 'Status', width: '130px' },
  { id: 'currency', label: 'Currency', width: '80px' },
  { id: 'amount', label: 'Amount', width: '120px' },
  { id: 'actions', label: '', width: '110px' },
];

/**
 * Type filter dropdown options (includes "All" option for no filter)
 */
const typeOptions = [
  { value: '', label: 'All' },
  { value: 'CR', label: 'Credit' },
  { value: 'DR', label: 'Debit' },
];

/**
 * Status filter dropdown options (multi-select)
 */
const statusOptions = [
  { value: EBankTransactionProcessingStatus.ACTION_REQUIRED, label: 'Action Required' },
  { value: EBankTransactionProcessingStatus.READY_TO_PROCESS, label: 'Ready To Process' },
  { value: EBankTransactionProcessingStatus.IN_PROGRESS, label: 'In Progress' },
  { value: EBankTransactionProcessingStatus.PROCESSED, label: 'Processed' },
];

/**
 * Custom hook to read/write URL search params (React Router v5 compatible)
 */
const useQueryParams = () => {
  const history = useHistory();
  const location = useLocation();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const setSearchParams = useCallback(
    (newParams: URLSearchParams, options?: { replace?: boolean }) => {
      const newSearch = newParams.toString();
      const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;

      if (options?.replace) {
        history.replace(newUrl);
      } else {
        history.push(newUrl);
      }
    },
    [history, location.pathname]
  );

  return [searchParams, setSearchParams] as const;
};

export const BankTransactionsPage: React.FC = () => {
  const userIsAdmin = useSelector(isAdmin);
  const userIsFinance = useSelector(isFinanceUser);
  const canImport = userIsAdmin || userIsFinance;
  const canDelete = userIsAdmin || userIsFinance;
  const canExport = userIsAdmin || userIsFinance;

  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useQueryParams();

  // Notifications
  const { showSuccessNotification, showErrorNotification, showWarningNotification } = useNotifications();

  // Delete modal state - stores UUID of transaction to delete
  const deleteModal = useModal<string>();

  // Delete mutation
  const deleteMutation = useDeleteBankTransaction();

  // Export hook (OWA-10235)
  const { exportBankTransactions, isExporting } = useExportBankTransactions();

  // Fetch payment method groups for bank filter dropdown
  const { data: paymentMethodGroups } = usePaymentMethodGroupsQuery();

  // Build bank options from payment method groups
  const bankOptions = useMemo(() => {
    if (!paymentMethodGroups) return [];
    return paymentMethodGroups
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(group => ({
        value: group.code,
        label: group.name,
      }));
  }, [paymentMethodGroups]);

  // Read filter values from URL params
  const startDateParam = searchParams.get('startDate');
  const endDateParam = searchParams.get('endDate');
  const paymentMethodGroupCodeParam = searchParams.get('bank');
  const typeParam = searchParams.get('type') as TBankTransactionTypeFilter | null;
  const statusesParam = searchParams.get('statuses');
  const searchParam = searchParams.get('search') || '';
  const bookingRefParam = searchParams.get('bookingRef') || '';
  const pageParam = searchParams.get('page');

  // Parse statuses from comma-separated string
  const statusesFromUrl: EBankTransactionProcessingStatus[] = statusesParam
    ? (statusesParam.split(',').filter(Boolean) as EBankTransactionProcessingStatus[])
    : [];

  // Debounced search input (500ms delay)
  const [searchTerm, setSearchTerm, debouncedSearchTerm, setDebouncedSearchTerm] = useDebounce(searchParam, 500);

  // Debounced booking ref input (500ms delay)
  const [bookingRefTerm, setBookingRefTerm, debouncedBookingRef, setDebouncedBookingRef] = useDebounce(
    bookingRefParam,
    500
  );

  // Effective search value (only use if 3+ chars)
  const effectiveSearch = debouncedSearchTerm.length >= 3 ? debouncedSearchTerm : '';

  // Effective booking ref value (only use if 3+ chars)
  const effectiveBookingRef = debouncedBookingRef.length >= 3 ? debouncedBookingRef : '';

  // Build filter object from URL params
  const filters: IBankTransactionFilters = useMemo(
    () => ({
      startDate: startDateParam,
      endDate: endDateParam,
      paymentMethodGroupCode: paymentMethodGroupCodeParam,
      type: typeParam,
      statuses: statusesFromUrl,
      search: effectiveSearch,
      bookingRef: effectiveBookingRef,
    }),
    [
      startDateParam,
      endDateParam,
      paymentMethodGroupCodeParam,
      typeParam,
      statusesParam,
      effectiveSearch,
      effectiveBookingRef,
    ]
  );

  // Pagination state (1-indexed) - read initial page from URL
  const initialPage = pageParam ? parseInt(pageParam, 10) : 1;
  const paginationData = usePagination({ initialPage, initialPerPage: 10, initialTotal: 0 });

  // Sorting state - default sort by date descending (newest first)
  const [sortBy, setSortBy] = React.useState('transactionDate');
  const [sortOrder, setSortOrder] = React.useState<ESortOrder>(ESortOrder.DESC);

  // Fetch data with React Query
  const { data, isLoading, isError, isFetching } = useBankTransactionsQuery(
    paginationData.page,
    paginationData.perPage,
    sortBy,
    sortOrder,
    filters
  );

  // Track if we've ever loaded data (stays true forever once set)
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // Update total when data loads, and mark as loaded once
  useEffect(() => {
    if (data?.meta.total !== undefined) {
      paginationData.setTotal(data.meta.total);
      if (!hasLoadedOnce) {
        setHasLoadedOnce(true);
      }
    }
  }, [data?.meta.total]);

  // Sync page number to URL when pagination changes
  useEffect(() => {
    const currentPageParam = searchParams.get('page');
    const currentPage = currentPageParam ? parseInt(currentPageParam, 10) : 1;
    if (paginationData.page !== currentPage) {
      const newParams = new URLSearchParams(searchParams);
      if (paginationData.page === 1) {
        newParams.delete('page');
      } else {
        newParams.set('page', String(paginationData.page));
      }
      setSearchParams(newParams, { replace: true });
    }
  }, [paginationData.page]);

  // Sync debounced search to URL
  useEffect(() => {
    const currentSearchParam = searchParams.get('search') || '';
    if (debouncedSearchTerm !== currentSearchParam) {
      const newParams = new URLSearchParams(searchParams);
      if (debouncedSearchTerm === '') {
        newParams.delete('search');
      } else {
        newParams.set('search', debouncedSearchTerm);
      }
      newParams.delete('page'); // Reset to page 1
      setSearchParams(newParams, { replace: true });
      paginationData.setPage(1);
    }
  }, [debouncedSearchTerm]);

  // Sync debounced booking ref to URL
  useEffect(() => {
    const currentBookingRefParam = searchParams.get('bookingRef') || '';
    if (debouncedBookingRef !== currentBookingRefParam) {
      const newParams = new URLSearchParams(searchParams);
      if (debouncedBookingRef === '') {
        newParams.delete('bookingRef');
      } else {
        newParams.set('bookingRef', debouncedBookingRef);
      }
      newParams.delete('page');
      setSearchParams(newParams, { replace: true });
      paginationData.setPage(1);
    }
  }, [debouncedBookingRef]);

  // Clean up queries on unmount
  useEffect(() => {
    return () => {
      queryClient.resetQueries({ queryKey: [BANK_TRANSACTIONS_QUERY_KEY] });
      queryClient.resetQueries({ queryKey: [PAYMENT_METHOD_GROUPS_QUERY_KEY] });
    };
  }, [queryClient]);

  // Helper to update a single filter in URL params
  const updateFilter = useCallback(
    (key: string, value: string | null) => {
      const newParams = new URLSearchParams(searchParams);
      if (value === null || value === '') {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
      newParams.delete('page'); // Reset pagination when filter changes
      setSearchParams(newParams, { replace: true });
      paginationData.setPage(1);
    },
    [searchParams, setSearchParams, paginationData]
  );

  // Filter change handlers
  const handleStartDateChange = useCallback(
    (date: Date) => {
      updateFilter('startDate', date ? format(date, 'yyyy-MM-dd') : null);
    },
    [updateFilter]
  );

  const handleEndDateChange = useCallback(
    (date: Date) => {
      updateFilter('endDate', date ? format(date, 'yyyy-MM-dd') : null);
    },
    [updateFilter]
  );

  const handleBankChange = useCallback(
    (values: string[]) => {
      updateFilter('bank', values[0] || null);
    },
    [updateFilter]
  );

  const handleTypeChange = useCallback(
    (values: string[]) => {
      updateFilter('type', values[0] || null);
    },
    [updateFilter]
  );

  const handleStatusChange = useCallback(
    (values: string[]) => {
      updateFilter('statuses', values.length > 0 ? values.join(',') : null);
    },
    [updateFilter]
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.currentTarget.value);
    },
    [setSearchTerm]
  );

  const handleBookingRefChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setBookingRefTerm(e.currentTarget.value);
    },
    [setBookingRefTerm]
  );

  // Reset all filters
  const handleResetFilters = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setBookingRefTerm('');
    setDebouncedBookingRef('');
    paginationData.setPage(1);
  }, [
    setSearchParams,
    setSearchTerm,
    setDebouncedSearchTerm,
    setBookingRefTerm,
    setDebouncedBookingRef,
    paginationData,
  ]);

  // Check if any filters are active
  const hasActiveFilters =
    startDateParam ||
    endDateParam ||
    paymentMethodGroupCodeParam ||
    typeParam ||
    statusesFromUrl.length > 0 ||
    searchTerm ||
    bookingRefTerm;

  // Handle sort
  const handleSort = (newSortBy: string, newSortOrder: string) => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder as ESortOrder);
  };

  // Helper to get display name for status
  const getDisplayStatus = (status: EBankTransactionProcessingStatus): string => {
    switch (status) {
      case EBankTransactionProcessingStatus.PROCESSED:
        return 'Processed';
      case EBankTransactionProcessingStatus.READY_TO_PROCESS:
        return 'Ready To Process';
      case EBankTransactionProcessingStatus.IN_PROGRESS:
        return 'In Progress';
      default:
        return status;
    }
  };

  // Handle delete click - show notification or modal based on status
  const handleDelete = useCallback(
    (uuid: string, status: EBankTransactionProcessingStatus) => {
      switch (status) {
        case EBankTransactionProcessingStatus.PROCESSED:
        case EBankTransactionProcessingStatus.READY_TO_PROCESS:
        case EBankTransactionProcessingStatus.IN_PROGRESS:
          showWarningNotification(`Cannot delete bank transaction with ${getDisplayStatus(status)} status`);
          break;
        case EBankTransactionProcessingStatus.ACTION_REQUIRED:
          deleteModal.showModal(uuid);
          break;
      }
    },
    [showWarningNotification, deleteModal]
  );

  // Handle delete confirmation
  const handleDeleteConfirm = useCallback(async () => {
    const uuid = deleteModal.modalData;
    if (!uuid) return;

    deleteModal.handleClose();

    try {
      await deleteMutation.mutateAsync(uuid);
      paginationData.setPage(1);
      showSuccessNotification('Bank transaction deleted.');
    } catch (error) {
      showErrorNotification('There was an error deleting bank transaction.');
    }
  }, [deleteModal, deleteMutation, paginationData, showSuccessNotification, showErrorNotification]);

  // Handle export (OWA-10235)
  // Note: Filter fields go directly in body (not nested), sort is hardcoded on BE
  const handleExport = useCallback(() => {
    // Convert type filter (CR/DR) to amountCents filter (positive/negative)
    const amountCentsFilter = typeParam === 'CR' ? 'positive' : typeParam === 'DR' ? 'negative' : undefined;

    exportBankTransactions({
      term: effectiveSearch || undefined,
      bookingRef: effectiveBookingRef || undefined,
      transactionDateFrom: startDateParam || undefined,
      transactionDateTo: endDateParam || undefined,
      paymentMethodGroupCode: paymentMethodGroupCodeParam || undefined,
      amountCents: amountCentsFilter,
      processingStatuses: statusesFromUrl.length > 0 ? statusesFromUrl : undefined,
    });
  }, [
    exportBankTransactions,
    effectiveSearch,
    effectiveBookingRef,
    startDateParam,
    endDateParam,
    paymentMethodGroupCodeParam,
    typeParam,
    statusesFromUrl,
  ]);

  // Transform data to table rows
  const tableRows: ITableRow[] = useMemo(() => {
    const transactions = data?.data || [];
    return transactions.map((tx: IBankTransaction) => ({
      id: tx.uuid,
      cells: [
        { id: 'date', value: formatTransactionDate(tx.transactionDate) },
        { id: 'bank', value: <BankCell paymentMethodGroup={tx.paymentMethodGroup} /> },
        { id: 'type', value: <TransactionTypeCell amountCents={tx.amountCents} /> },
        { id: 'reference', value: <TruncatedTooltipCell value={tx.reference} maxWidth="80px" /> },
        { id: 'narration', value: <TruncatedTooltipCell value={tx.narration} maxWidth="180px" /> },
        {
          id: 'counterparty',
          value: tx.counterparty?.name ?? <span className="text-gray-60">—</span>,
        },
        { id: 'status', value: <TransactionStatusCell status={tx.processingStatus} /> },
        { id: 'currency', value: tx.currency },
        {
          id: 'amount',
          value: (
            <span className={isDebit(tx.amountCents) ? 'text-red-90' : ''}>
              {isDebit(tx.amountCents) ? '-' : ''}
              {formatAmount(tx.amountCents)}
            </span>
          ),
        },
        {
          id: 'actions',
          value: (
            <ActionsCell
              transactionUuid={tx.uuid}
              amountCents={tx.amountCents}
              processingStatus={tx.processingStatus}
              onDelete={handleDelete}
              canDelete={canDelete}
            />
          ),
        },
      ],
    }));
  }, [data?.data, handleDelete, canDelete]);

  const total = data?.meta.total || 0;

  return (
    <div className="w-auto mx-[20px] crm-desktop:mx-[80px]">
      {/* Header with title */}
      <div className="mb-6">
        <PageTitle
          primaryText="Bank Transactions"
          secondaryCondition={hasLoadedOnce}
          secondaryText={getResultsText(total)}
          loadingCondition={isFetching && hasLoadedOnce}
          loadingText="Loading ..."
        />
      </div>

      {/* Error state */}
      {isError && <ErrorBar />}

      {/* Initial loading state - only shown before first successful load */}
      {!isError && !hasLoadedOnce && <LoadingBar />}

      {/* Filters and Table - show after first load, stays visible during all refetches */}
      {!isError && hasLoadedOnce && (
        <>
          {/* Filter Results Section */}
          <div className="mb-5">
            <span className="block font-pt-sans text-[13px] text-black mb-3 font-bold">Filter Results</span>

            {/* Filter row */}
            <div className="flex flex-wrap gap-5 items-end">
              {/* Search */}
              <div className="flex flex-col flex-1 min-w-[180px]">
                <span className="font-pt-sans text-[13px] text-black mb-[5px]">Search</span>
                <TextInput
                  id="bank-transactions-search"
                  className="w-full bg-ivory"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder="Search..."
                />
              </div>

              {/* Booking Ref */}
              <div className="flex flex-col w-[190px]">
                <span className="font-pt-sans text-[13px] text-black mb-[5px]">Booking Ref.</span>
                <TextInput
                  id="bank-transactions-booking-ref"
                  className="w-full bg-ivory"
                  value={bookingRefTerm}
                  onChange={handleBookingRefChange}
                  placeholder="Search..."
                />
              </div>

              {/* Start Date */}
              <div className="flex flex-col w-[120px]">
                <span className="font-pt-sans text-[13px] text-black mb-[5px]">Start Date</span>
                <SingleDateInput
                  buttonClassName="!normal-case !h-[35px] !bg-ivory !border-gray-40"
                  displayStringClassName="!font-pt-sans"
                  value={startDateParam ? parseISO(startDateParam) : null}
                  onChange={handleStartDateChange}
                  enablePastDates={true}
                  placeholder="Select date..."
                  placeholderClassName="italic text-gray-100"
                  calendarCount={1}
                />
              </div>

              {/* End Date */}
              <div className="flex flex-col w-[120px]">
                <span className="font-pt-sans text-[13px] text-black mb-[5px]">End Date</span>
                <SingleDateInput
                  buttonClassName="!normal-case !h-[35px] !bg-ivory !border-gray-40"
                  displayStringClassName="!font-pt-sans"
                  value={endDateParam ? parseISO(endDateParam) : null}
                  onChange={handleEndDateChange}
                  enablePastDates={true}
                  placeholder="Select date..."
                  placeholderClassName="italic text-gray-100"
                  calendarCount={1}
                />
              </div>

              {/* Bank */}
              <div className="flex flex-col">
                <span className="font-pt-sans text-[13px] text-black mb-[5px]">Bank</span>
                <Multiselect
                  className="bg-ivory min-w-[180px]"
                  itemsClassname="bg-ivory"
                  itemCtaClassName="hover:bg-gray-10"
                  options={bankOptions}
                  selectedValues={paymentMethodGroupCodeParam ? [paymentMethodGroupCodeParam] : []}
                  onUpdate={handleBankChange}
                  isSingleSelectMode={true}
                  isCloseOnSelect={true}
                  hideCheckboxes={true}
                  placeholderText="All"
                />
              </div>

              {/* Type */}
              <div className="flex flex-col">
                <span className="font-pt-sans text-[13px] text-black mb-[5px]">Type</span>
                <Multiselect
                  className="bg-ivory min-w-[120px]"
                  itemsClassname="bg-ivory"
                  itemCtaClassName="hover:bg-gray-10"
                  options={typeOptions}
                  selectedValues={[typeParam || '']}
                  onUpdate={handleTypeChange}
                  isSingleSelectMode={true}
                  isCloseOnSelect={true}
                  hideCheckboxes={true}
                  placeholderText="All"
                />
              </div>

              {/* Status */}
              <div className="flex flex-col">
                <span className="font-pt-sans text-[13px] text-black mb-[5px]">Status</span>
                <Multiselect
                  className="bg-ivory min-w-[180px]"
                  itemsClassname="bg-ivory"
                  itemCtaClassName="hover:bg-gray-10"
                  options={statusOptions}
                  selectedValues={statusesFromUrl}
                  onUpdate={handleStatusChange}
                  hideCheckboxes={false}
                  placeholderText="All"
                />
              </div>
            </div>

            {/* Reset Filters and Export/Import buttons */}
            <div className="mt-4 flex justify-between items-center">
              <FluidButton type="secondary" onClick={handleResetFilters} disabled={!hasActiveFilters}>
                Reset Filters
              </FluidButton>
              <div className="flex gap-3">
                {canExport && (
                  <FluidButton type="secondary" onClick={handleExport} isLoading={isExporting}>
                    Export CSV
                  </FluidButton>
                )}
                {canImport && (
                  <Link to="/finance/bank-transactions/import">
                    <FluidButton type="primary">Import</FluidButton>
                  </Link>
                )}
              </div>
            </div>
          </div>

          <Table
            columns={tableColumns}
            rows={tableRows}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            loading={isFetching || deleteMutation.isPending}
            messageWhenEmpty="No Results"
            className="mt-5"
            evenColor="white-hard"
          />

          {/* Pagination - only show when there are results */}
          {tableRows.length >= 1 && (
            <Pagination
              className="mt-5"
              currentPage={paginationData.page}
              itemsPerPage={paginationData.perPage}
              onItemsPerPageChange={paginationData.setPerPage}
              onPageSelect={paginationData.setPage}
              pageCount={paginationData.pageCount}
            />
          )}
        </>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        type={EConfirmationModalType.WARNING}
        isOpen={deleteModal.isOpen}
        title="You are going to delete a bank transaction."
        message="Are you sure want to delete this bank transaction?"
        confirmButtonLabel="Yes"
        cancelButtonLabel="No"
        onConfirm={handleDeleteConfirm}
        onCancel={deleteModal.handleClose}
        isConfirmLoading={deleteMutation.isPending}
      />
    </div>
  );
};
